import { type Control, type IEvent } from 'fabric/fabric-impl';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { AttachmentPoint, type Constructor } from '../types';
import { distanceBetweenPointsIsGreaterThan } from '../utils/geometry';
import { isEndlessStack } from './guards';

interface EndlessStackBehaviors {
  endlessStack: boolean;
  // How many pixels mouse has to drag from a stack to create a new shape
  stackDragThreshold: number;
  dragFromStack(e: MouseEvent): typeof this | undefined;
}

// Behaviors for create a stack of endless copies of the shape
// eslint-disable-next-line @typescript-eslint/no-redeclare
function EndlessStackBehaviors(Base: Constructor<fabric.Object>): any {
  return class WithEndlessStackBehaviors
    extends Base
    implements EndlessStackBehaviors
  {
    __hasEndlessStackBehaviors = true;
    serializedKeys = [
      // @ts-expect-error serializedKeys is declared on subclasses
      ...this.serializedKeys,
      'endlessStack',
    ];

    stackDragThreshold: number = 16;
    endlessStack: boolean;

    private readonly noteControls: { [key: string]: Control };
    private readonly stackControls: { [key: string]: Control };

    constructor(options?: object) {
      super(options);

      this.noteControls = this.controls;
      this.stackControls = { ...this.controls };
      delete this.stackControls[AttachmentPoint.Left];
      delete this.stackControls[AttachmentPoint.Right];
      delete this.stackControls[AttachmentPoint.Top];
      delete this.stackControls[AttachmentPoint.Bottom];

      if (options && 'endlessStack' in options && options.endlessStack) {
        this.setEndlessStack(true);
      }

      this.on('mousedown:before', e => this.onStackMouseDown(e));
    }

    // Duplicate the shape and start dragging it with the mouse
    dragFromStack(e: MouseEvent): typeof this | undefined {
      const shape = this.canvas.duplicateObjects(this) as typeof this;
      if (!shape) return;

      shape.set({
        endlessStack: false,
        locked: false,
      });
      shape.bringToFront();

      const mousePointer = this.canvas.getUncachedPointer(e);
      const width = this.getScaledWidth();
      const height = this.getScaledHeight();
      shape.moveToCoords({
        left: mousePointer.x - width / 2,
        top: mousePointer.y - height / 2,
      });

      shape.canvas._setupCurrentTransform(e, shape, true);
      // Simulate a mouse down on the new shape and let fabric take over
      shape.canvas._onMouseDown(e);

      return shape;
    }

    showCommentTool() {
      // Hide comment tool on endless stacks
      return !isEndlessStack(this);
    }

    _set(keyOrPath: KeyPath, value: any) {
      if (keyOrPath === 'endlessStack') {
        this.setEndlessStack(!!value);
      }
      return super._set(keyOrPath, value);
    }

    private setEndlessStack(endlessStack: boolean) {
      this.lockMovementX = endlessStack;
      this.lockMovementY = endlessStack;
      this.activeOn = endlessStack ? 'up' : 'down';
      this.canvas?.requestRenderAll();
      this.controls = endlessStack ? this.stackControls : this.noteControls;
    }

    private onStackMouseDown({ e }: IEvent<MouseEvent>) {
      if (!this.endlessStack) {
        // Not an endless stack
        return;
      }
      if (e.altKey || e.button !== 0) {
        // Ignore anything but left click
        return;
      }
      if (this.__corner) {
        // Ignore events on resize controls
        return;
      }
      if ('isDraggingFromMenu' in this && this.isDraggingFromMenu) {
        // Dragging from the menu button
        return;
      }

      // Ensure correct locking when starting to drag
      this.setEndlessStack(this.endlessStack);

      const oldSelection = this.canvas.selection;
      // Temporarily disable drag selection on locked stacks so we can still drag off a new shape
      if (this.locked) {
        this.canvas.selection = false;
      }

      const { clientX: x, clientY: y } = e;
      const dragStartPosition = { x, y };

      const cleanup = () => {
        window.removeEventListener('mouseup', cleanup);
        window.removeEventListener('mousemove', onMouseMove);
        this.canvas.selection = oldSelection;
      };

      const onMouseMove = (e: MouseEvent) => {
        const { clientX: x, clientY: y } = e;
        const currentPosition = { x, y };
        const passedThreshold = distanceBetweenPointsIsGreaterThan(
          dragStartPosition,
          currentPosition,
          this.stackDragThreshold
        );
        if (passedThreshold) {
          cleanup();
          this.dragFromStack(e);
        }
      };

      window.addEventListener('mouseup', cleanup);
      window.addEventListener('mousemove', onMouseMove);
    }
  };
}

export default EndlessStackBehaviors;
